/**
 * REUSABLE HOOK - usePagination
 * Custom hook for pagination management
 * Can be used in any React project with paginated data
 */

import { useState, useMemo } from 'react';

/**
 * usePagination - Pagination hook
 * @param {Array} data - Data to paginate
 * @param {number} initialPageSize - Initial page size (default: 25)
 * @returns {Object} Pagination state and methods
 */
export const usePagination = (data = [], initialPageSize = 25) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data.length, pageSize]);

  // Get current page data
  const currentData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  // Go to specific page
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Go to next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  // Go to previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  // Go to first page
  const firstPage = () => {
    setCurrentPage(1);
  };

  // Go to last page
  const lastPage = () => {
    setCurrentPage(totalPages);
  };

  // Change page size
  const changePageSize = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  // Reset pagination
  const reset = () => {
    setCurrentPage(1);
    setPageSize(initialPageSize);
  };

  // Check if there are pages
  const hasPages = totalPages > 1;

  // Check if can go to next page
  const canGoNext = currentPage < totalPages;

  // Check if can go to previous page
  const canGoPrevious = currentPage > 1;

  // Get pagination info
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, data.length);

  return {
    // Data
    currentData,
    allData: data,

    // Page info
    currentPage,
    pageSize,
    totalPages,
    totalItems: data.length,
    startIndex,
    endIndex,

    // Navigation
    goToPage,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    changePageSize,
    reset,

    // States
    hasPages,
    canGoNext,
    canGoPrevious,
    isFirstPage: currentPage === 1,
    isLastPage: currentPage === totalPages,
  };
};

export default usePagination;
